import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { DSLinkButton } from '@stihl-design-system/components';

import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import { Season } from '@phoenix/team-m/dashboard/frontend/shared/use-user-preferences-context';
import { GA } from '@phoenix/core/frontend/shared/feature/google-analytics/ga';
import { useImpressionEvent } from '@phoenix/core/frontend/shared/util/hooks/use-impression-event';
import { BaseBanner } from '@phoenix/team-m/dashboard/frontend/shared/components/base-banner';

interface GardenCareBannerProps {
  season: Season | null;
  showButton?: boolean;
  trackEvents?: boolean;
  className?: string;
}

export const GardenCareBanner: React.FC<GardenCareBannerProps> = ({ season, showButton, trackEvents, className }) => {
  const { t } = useTranslation();

  const background = {
    [Season.SPRING]: 'season-spring.webp',
    [Season.SUMMER]: 'season-summer.webp',
    [Season.AUTUMN]: 'season-fall.webp',
    [Season.WINTER]: 'season-winter.webp',
  };

  const headline = {
    [Season.SPRING]: 'dashboard_myGuidesBannerSpring_header',
    [Season.SUMMER]: 'dashboard_myGuidesBannerSummer_header',
    [Season.AUTUMN]: 'dashboard_myGuidesBannerAutumn_header',
    [Season.WINTER]: 'dashboard_myGuidesBannerWinter_header',
  };

  const BASE_EVENT_NAME = 'mystihl_banner_interactive_garden';

  const gardenCareBannerLinkClick = () => {
    trackEvents &&
      GA.sendEvent({
        event_name: `${BASE_EVENT_NAME}_link_click`,
        ...trackingData,
      });
  };

  const linkButton = showButton
    ? {
        url: link('dashboard/my-guides'),
        label: t('dashboard_myGuidesBanner_buttonLabel'),
      }
    : undefined;

  const trackingData = {
    id: 'garden-care-banner',
    headline: t('dashboard_myGuidesBannerDefault_header'),
    description: t('dashboard_myGuidesBannerDefault_description'),
    button_text: linkButton?.label,
    link_url: linkButton?.url,
  };

  const ref = useImpressionEvent(() => trackEvents && GA.sendEvent({ event_name: `${BASE_EVENT_NAME}_impression`, ...trackingData }));

  return (
    <BaseBanner
      ref={ref}
      id="garden-care-banner"
      backgroundImg={season ? background[season] : 'season-spring.webp'}
      isAnimated={true}
      imageTags={['imagesDashboard']}
      headline={t(season ? headline[season] : 'dashboard_myGuidesBannerDefault_header')}
      description={t('dashboard_myGuidesBannerDefault_description')}
      className={className}
    >
      {linkButton && (
        <Link href={linkButton.url}>
          <DSLinkButton onClick={gardenCareBannerLinkClick} className="mt-5 w-full tablet:w-fit">
            {linkButton.label}
          </DSLinkButton>
        </Link>
      )}
    </BaseBanner>
  );
};

export default GardenCareBanner;
